'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { ShoppingCart, Plus, Search, Download, Eye, Printer, ArrowLeft, Package, X,  } from 'lucide-react';
import {
  MOCK_PURCHASE_ORDERS, PO_STATUS_STYLES, formatUGX, PurchaseOrder, POStatus,
} from '../../components/procurementData';
import { useRole } from '@/lib/useRole';
import BudgetTracker, { MOCK_BUDGETS, BudgetSummaryPanel } from '../../components/BudgetTracker';

function POStatusBadge({ status }: { status: POStatus }) {
  const st = PO_STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
      {st.label}
    </span>
  );
}

// Department → budget code mapping
const DEPT_BUDGET_MAP: Record<string, string> = {
  'Feed Management': 'BC-FEED-2026',
  'Health & Veterinary': 'BC-HEALTH-2026',
  'Farm Operations': 'BC-OPS-2026',
  'Administration': 'BC-ADMIN-2026',
  'Maintenance': 'BC-MAINT-2026',
};

function PODrawer({ po, onClose }: { po: PurchaseOrder; onClose: () => void }) {
  const subtotal = po.items.reduce((s, i) => s + i.lineTotal, 0);
  const budgetCode = DEPT_BUDGET_MAP[po.department];

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-base font-bold text-gray-900">{po.poNumber}</div>
            <div className="text-xs text-gray-400 mt-0.5">{po.supplier} · {po.purchaseDate}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <POStatusBadge status={po.status} />
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">{po.currency}</span>
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600">{po.paymentTerms}</span>
          </div>

          {/* Budget Tracker */}
          {budgetCode && (
            <BudgetTracker
              budgetCode={budgetCode}
              requestAmount={po.status === 'pending_approval' ? po.grandTotal : 0}
              currency={po.currency}
              compact={po.status !== 'pending_approval'}
            />
          )}

          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Buyer', value: po.buyer },
              { label: 'Farm', value: po.farm },
              { label: 'Department', value: po.department },
              { label: 'Expected Delivery', value: po.expectedDeliveryDate },
              { label: 'Delivery Terms', value: po.deliveryTerms },
              { label: 'Requisition', value: po.requisitionNumber || '—' },
            ].map(f => (
              <div key={f.label}>
                <div className="text-xs text-gray-400 font-medium">{f.label}</div>
                <div className="text-sm font-semibold text-gray-800 mt-0.5">{f.value}</div>
              </div>
            ))}
          </div>

          <div>
            <div className="text-xs font-bold text-gray-700 mb-1">Delivery Address</div>
            <div className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3">{po.deliveryAddress}</div>
          </div>

          {/* Items Table */}
          <div>
            <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Order Items</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 text-gray-500 font-semibold">Item</th>
                    <th className="text-right py-2 text-gray-500 font-semibold">Qty</th>
                    <th className="text-right py-2 text-gray-500 font-semibold">Unit Price</th>
                    <th className="text-right py-2 text-gray-500 font-semibold">Disc%</th>
                    <th className="text-right py-2 text-gray-500 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {po.items.map(item => (
                    <tr key={item.id}>
                      <td className="py-2">
                        <div className="font-semibold text-gray-800">{item.description}</div>
                        <div className="text-gray-400">{item.category} · {item.unit}</div>
                      </td>
                      <td className="py-2 text-right text-gray-700">{item.quantity}</td>
                      <td className="py-2 text-right text-gray-700">{formatUGX(item.unitPrice)}</td>
                      <td className="py-2 text-right text-gray-500">{item.discount}%</td>
                      <td className="py-2 text-right font-bold text-gray-900">{formatUGX(item.lineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-3 space-y-1.5 border-t border-gray-100 pt-3">
              {[
                { label: 'Subtotal', value: po.subtotal },
                { label: 'Discount', value: -po.discount },
                { label: 'Tax', value: po.tax },
                { label: 'Delivery Charges', value: po.deliveryCharges },
              ].map(row => (
                <div key={row.label} className="flex justify-between text-xs text-gray-600">
                  <span>{row.label}</span>
                  <span className={row.value < 0 ? 'text-green-600' : ''}>{row.value < 0 ? `- ${formatUGX(-row.value)}` : formatUGX(row.value)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-bold text-gray-900 border-t border-gray-100 pt-2 mt-2">
                <span>Grand Total</span>
                <span>{formatUGX(po.grandTotal)}</span>
              </div>
            </div>
          </div>

          {po.notes && (
            <div>
              <div className="text-xs font-bold text-gray-700 mb-1">Notes</div>
              <div className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3">{po.notes}</div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
          <button className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-xs font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            <Printer size={13} /> Print PO
          </button>
          <button className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-xs font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            <Download size={13} /> Export PDF
          </button>
          <Link href="/procurement/goods-received/new" className="ml-auto flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-xl hover:bg-teal-700 transition-colors">
            <Package size={13} /> Receive Goods
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function PurchaseOrdersContent() {
  const { can } = useRole();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<PurchaseOrder | null>(null);
  const [orders, setOrders] = useState(MOCK_PURCHASE_ORDERS);

  const canCreate = can('manage_procurement');

  const filtered = useMemo(() => {
    return orders.filter(po => {
      const q = search.toLowerCase();
      const matchSearch = !q || po.poNumber.toLowerCase().includes(q) || po.supplier.toLowerCase().includes(q) || po.department.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || po.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [orders, search, statusFilter]);

  const stats = {
    total: orders.length,
    pending: orders.filter(o => ['pending_approval', 'approved'].includes(o.status)).length,
    partial: orders.filter(o => o.status === 'partially_delivered').length,
    completed: orders.filter(o => ['fully_delivered', 'closed'].includes(o.status)).length,
    totalSpend: orders.reduce((s, o) => s + o.grandTotal, 0),
  };

  // Budget codes for all departments with active POs
  const activePOBudgetCodes = [...new Set(
    orders
      .filter(o => ['pending_approval', 'approved', 'issued', 'partially_delivered'].includes(o.status))
      .map(o => DEPT_BUDGET_MAP[o.department])
      .filter(Boolean)
  )];

  return (
    <div className="px-4 md:px-6 xl:px-8 py-6 max-w-screen-2xl mx-auto">

      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <Link href="/procurement" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={16} className="text-gray-500" />
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-xs text-gray-400 mt-0.5">Manage supplier purchase orders — Idudi Poultry Farm</p>
        </div>
        {canCreate && (
          <Link href="/procurement/requisitions/new" className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors shadow-sm">
            <Plus size={14} /> New PO
          </Link>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 my-5">
        {[
          { label: 'Total Orders', value: stats.total, color: 'text-gray-900' },
          { label: 'Pending/Approved', value: stats.pending, color: 'text-amber-600' },
          { label: 'Partial Delivery', value: stats.partial, color: 'text-orange-600' },
          { label: 'Completed', value: stats.completed, color: 'text-green-600' },
          { label: 'Total Spend', value: `${(stats.totalSpend / 1000000).toFixed(1)}M`, color: 'text-indigo-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Budget Summary Panel */}
      {activePOBudgetCodes.length > 0 && (
        <div className="mb-5">
          <BudgetSummaryPanel budgetCodes={activePOBudgetCodes} showSettings={canCreate} />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search purchase orders..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white text-gray-700">
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="issued">Issued</option>
          <option value="partially_delivered">Partially Delivered</option>
          <option value="fully_delivered">Fully Delivered</option>
          <option value="closed">Closed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button className="flex items-center gap-1.5 px-3 py-2.5 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-600">
          <Download size={14} /> Export
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">PO Number</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Supplier</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Department</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Expected Delivery</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Grand Total</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-400">
                    <ShoppingCart size={32} className="mx-auto mb-2 text-gray-200" />
                    No purchase orders found.
                  </td>
                </tr>
              ) : filtered.map(po => (
                <tr key={po.id} className="hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => setSelected(po)}>
                  <td className="px-4 py-3">
                    <div className="font-bold text-gray-900 text-xs">{po.poNumber}</div>
                    <div className="text-xs text-gray-400">{po.items.length} item{po.items.length !== 1 ? 's' : ''}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs font-semibold text-gray-800">{po.supplier}</div>
                    <div className="text-xs text-gray-400">{po.supplierCode}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700">{po.department}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{po.expectedDeliveryDate}</td>
                  <td className="px-4 py-3 text-right text-xs font-bold text-gray-900">{formatUGX(po.grandTotal)}</td>
                  <td className="px-4 py-3"><POStatusBadge status={po.status} /></td>
                  <td className="px-4 py-3 text-xs text-gray-400">{po.purchaseDate}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={e => { e.stopPropagation(); setSelected(po); }} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="View">
                        <Eye size={13} className="text-gray-500" />
                      </button>
                      {['approved', 'issued', 'partially_delivered'].includes(po.status) && (
                        <Link href="/procurement/goods-received/new" onClick={e => e.stopPropagation()} className="p-1.5 rounded-lg hover:bg-teal-50 transition-colors" title="Receive Goods">
                          <Package size={13} className="text-teal-600" />
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-400">{filtered.length} of {orders.length} purchase orders</span>
          <div className="flex items-center gap-1">
            <button className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40" disabled>Previous</button>
            <span className="px-3 py-1.5 text-xs bg-orange-600 text-white rounded-lg font-semibold">1</span>
            <button className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40" disabled>Next</button>
          </div>
        </div>
      </div>

      {selected && <PODrawer po={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
